import { matchPath } from 'react-router'

import axios from 'axios'
import { pickBy } from 'lodash-es'
import { Store } from 'redux'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import { GOODNESS_ESSENTIALS_URL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  API_REQUEST_HEADERS,
  setTracingRequestHeaders,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/requestHeaderConstants'
import {
  AUTH_LANDING_VALUE,
  PAGE_ROUTES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { GOOGLE_PERFORMANCE_URL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/urlsToHandleWithInternalNavigation'
import { UTM_CAMPAIGN } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'
import { clearStorageDataOnLogout } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/logoutUtils'
import { getDevFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

import { setIsLogoutInProgress } from '../../data/logout/logoutActions'
import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { getSessionId } from '../../data/session/sessionSelectors'
import { getId, getUserDisplayLanguageOrDefault } from '../../data/user/userSelectors'
import { CSRF_HEADER_NAME, CSRF_TOKEN } from '../constants/appConstants'
import { clearPendoSession } from '../utils/pendoLoader'
import { updateStoredProviderResultsSurveyValues } from '../utils/pendoUtils'
import { logToSumoLogic, SUMO_CATEGORY } from '../utils/userUtils'

const NO_X_FEATURES_URLS = [/(?:https?:\/\/)vendorapi\.([a-zA-Z0-9-]+\.)?lyrahealth\.com\/.*/]

// baseURL Works in almost all cases, may need to be overridden a few times
const instance = axios.create({
  baseURL: '/services',
})

export const essentialsAxiosInstance = axios.create({
  baseURL: GOODNESS_ESSENTIALS_URL,
})

const SKIP_401_REDIRECT_URLS = ['/unsecured/v1/connected-services/exchange-initial-token']

const interceptor = (store: Store): void => {
  instance.interceptors.response.use(
    function (response) {
      return response
    },
    function (error) {
      const requestUrl = error.config?.url
      const shouldSkip401Redirect = requestUrl && SKIP_401_REDIRECT_URLS.some((url) => requestUrl.includes(url))
      if (error.response && error.response.status === 401 && !shouldSkip401Redirect) {
        const hostname = window.location.hostname
        let mixpanelEntryPoint = ''
        if (!matchPath('register/mfa/:id/verify', window.location.pathname)) {
          const redirectUrl = `${window.location.pathname + window.location.search + window.location.hash}`
          window.sessionStorage.setItem(UNAUTHENTICATED_REDIRECT_URL, redirectUrl)
          if (redirectUrl.includes(GOOGLE_PERFORMANCE_URL)) {
            mixpanelEntryPoint = UTM_CAMPAIGN.GOOGLE_CRITICAL_TALENT
          } else if (redirectUrl.includes(PAGE_ROUTES.PARENTAL_LEAVE_COACHING)) {
            mixpanelEntryPoint = COMMON_MIXPANEL_PAGE.PARENTAL_LEAVE_COACHING
          }
        }
        store.dispatch(setIsLogoutInProgress(true))
        mixpanel.reset()
        clearPendoSession()
        updateStoredProviderResultsSurveyValues()
        clearStorageDataOnLogout()
        const searchParams = new URLSearchParams(window.location.search)
        let authLanding = searchParams.get('authLanding')
        let targetUrl = ''
        const additionalQueryParams = []
        const defaultAuthLanding = {
          [PAGE_ROUTES.GOOGLE_PERFORMANCE]: AUTH_LANDING_VALUE.REGISTRATION,
          [PAGE_ROUTES.PARENTAL_LEAVE_COACHING]: AUTH_LANDING_VALUE.REGISTRATION,
        }

        const getUrl = (targetUrl: string, additionalQueryParams?: string[]) => {
          // persist query params (including utm params)
          const hasExistingParams = window.location.search.length > 0
          const separator = hasExistingParams ? '&' : '?'
          return `${targetUrl}${window.location.search}${
            additionalQueryParams?.length ? `${separator}${additionalQueryParams.join('&')}` : ''
          }`
        }

        if (window.location.pathname.includes(PAGE_ROUTES.ESSENTIALS)) {
          if (window.location.pathname.includes('articles')) {
            const contentSlug = window.location.pathname.split('/').pop()
            targetUrl = `${PAGE_ROUTES.ESSENTIALS_ARTICLES}/${contentSlug}`
          } else {
            targetUrl = PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET
          }
          window.location.replace(getUrl(targetUrl))
        } else {
          if (!authLanding) {
            authLanding = defaultAuthLanding[window.location.pathname]
          }
          if (authLanding === AUTH_LANDING_VALUE.REGISTRATION) {
            targetUrl = '/register/connect'
          } else if (hostname.startsWith('care')) {
            // If on care subdomain, redirect to landing page
            targetUrl = '/'
            additionalQueryParams.push('findEmployerSubmitRedirect=register/connect')
          } else {
            targetUrl = '/login'
          }
          if (mixpanelEntryPoint) {
            additionalQueryParams.push(`${COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT}=${mixpanelEntryPoint}`)
          }
          window.location.href = getUrl(targetUrl, additionalQueryParams)
        }
      }
      const sourceCategory =
        process.env.NODE_ENV === 'production' ? SUMO_CATEGORY.PROD_DEBUG : SUMO_CATEGORY.STAGING_DEBUG
      const state = store.getState()
      const lyraId = getId(state)
      logToSumoLogic(sourceCategory, lyraId, {
        messsage: `Request Error - ${error}`,
        type: 'error',
        error,
        sourceCategory: process.env.NODE_ENV === 'production' ? SUMO_CATEGORY.PROD_DEBUG : SUMO_CATEGORY.STAGING_DEBUG,
      })
      return Promise.reject(error)
    },
  )

  instance.interceptors.request.use(function (config) {
    const csrfHeaderName = localStorage.getItem(CSRF_HEADER_NAME)
    const csrfToken = localStorage.getItem(CSRF_TOKEN)
    if (csrfHeaderName && csrfToken && config.headers) {
      config.headers[csrfHeaderName] = csrfToken
    }

    // Pass dev flags from test hooks to LW API via response header
    const { url } = config
    const skipFeaturesHeader = !url || NO_X_FEATURES_URLS.some((regex) => regex.test(url))
    const enabledFeatureFlags = Object.keys(pickBy(getDevFlags(), (value) => value === true))
    if (!skipFeaturesHeader && enabledFeatureFlags.length > 0 && config.headers) {
      config.headers[API_REQUEST_HEADERS.X_FEATURES] = enabledFeatureFlags.join(',')
    }
    const state = store.getState()

    const sessionId = getSessionId(state)
    const userDisplayLanguage = getUserDisplayLanguageOrDefault(state)
    if (userDisplayLanguage && config.headers) {
      config.headers[API_REQUEST_HEADERS.X_LYRA_LOCALE] = userDisplayLanguage
    }
    setTracingRequestHeaders(config, sessionId)
    return config
  })
}

export default {
  interceptor,
}

export const axiosInstance = instance
